import { logEvent } from 'firebase/analytics';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { analyticsInstanceSelector, callSelector, setCallParticipants, subscribedGameIdSelector, subscriptionEpochSelector } from '../services/GameSlice';
import { emitCommand, getSocket } from '../socket';
import CallSession, { autoJoinTarget } from './CallSession';

const NOBODY = [];

const IDLE = {
    status: 'idle',
    askingPermission: false,
    error: null,
    notice: null,
    localStream: null,
    hasAudio: false,
    hasVideo: false,
    audioOn: false,
    videoOn: false,
    peers: []
};

/**
 * The video call for one game, for as long as the component using it is on
 * screen. `eligible` is whether this player may be in the call at all (a
 * seated human, with someone else human to talk to).
 */
const useCall = (gameId, eligible, userId) => {
    const dispatch = useDispatch();
    const analytics = useSelector(analyticsInstanceSelector);
    const call = useSelector(callSelector);
    const subscribedGameId = useSelector(subscribedGameIdSelector);
    const epoch = useSelector(subscriptionEpochSelector);
    const [state, setState] = useState(IDLE);
    const sessionRef = useRef(null);
    const analyticsRef = useRef(analytics);
    analyticsRef.current = analytics;

    // One session per game. Changing games hangs up the old call — unless a
    // rematch carried it across (see carryTo), in which case the session
    // already belongs to the new game and simply carries on.
    useEffect(() => {
        if (!gameId) {
            return;
        }
        if (sessionRef.current?.gameId === String(gameId)) {
            return;
        }
        sessionRef.current?.destroy();
        const session = new CallSession({
            gameId: String(gameId),
            socket: getSocket(),
            command: emitCommand,
            onChange: setState,
            onEvent: (name, params) => {
                if (analyticsRef.current) {
                    logEvent(analyticsRef.current, name, { gameId: session.gameId, ...params });
                }
            }
        });
        sessionRef.current = session;
        setState(session.snapshot());
    }, [gameId]);

    useEffect(() => () => {
        sessionRef.current?.destroy();
        sessionRef.current = null;
    }, []);

    // Until the server's list for this game arrives, there is nothing to go
    // on — not "nobody is here", which would hang up on everyone. Right after
    // a rematch carried the call, the page still shows the old game for a
    // moment; the call already belongs to the new one, so follow it there
    // rather than show nobody (which would drop everyone's picture).
    const session = sessionRef.current;
    const carried = session && session.carriedFrom === String(gameId) && session.gameId !== String(gameId);
    const callGameId = carried ? session.gameId : String(gameId);
    const current = call?.gameId === callGameId;
    const participants = current ? call.participants : NOBODY;
    useEffect(() => {
        if (current) {
            sessionRef.current?.syncParticipants(participants);
        }
    }, [participants, current]);

    // Join by ourselves after a reload or a dropped connection, or when a
    // rematch carried us over from the last game's call. At most one attempt
    // per subscription, so a call that has since filled up is not retried in
    // a loop.
    const attemptedFor = useRef(null);
    const subscribed = Boolean(gameId) && subscribedGameId === gameId;
    useEffect(() => {
        const session = sessionRef.current;
        if (!session || !subscribed || !eligible || attemptedFor.current === epoch) {
            return;
        }
        const wanted = session.status === 'reconnecting' || (session.status === 'idle' && autoJoinTarget() === gameId);
        if (wanted) {
            attemptedFor.current = epoch;
            session.join();
        }
    }, [subscribed, eligible, epoch, gameId, state.status]);

    // Out of the call's reach: the window got too narrow for the board, or
    // the seat went to a computer. Hang up, but come back by ourselves if it
    // becomes possible again. Only judged once subscribed: on the way into a
    // rematch the seat is briefly unknown, and that is not a reason to go.
    useEffect(() => {
        const session = sessionRef.current;
        if (session && subscribed && !eligible && session.status !== 'idle') {
            session.leave({ rejoinLater: true });
            attemptedFor.current = null;
        }
    }, [eligible, subscribed]);

    // ------------------------------------------------------------- ringing
    // Someone started the call: ring here until it times out, this player
    // joins or turns it down.
    const ring = current ? call.ringing : null;
    const [ringUntil, setRingUntil] = useState(0);
    const [dismissedRing, setDismissedRing] = useState(null);
    const ringKey = ring ? ring.id : null;
    useEffect(() => {
        if (!ring) {
            setRingUntil(0);
            return undefined;
        }
        const until = Date.now() + ring.remainingMs;
        setRingUntil(until);
        const timer = setTimeout(() => setRingUntil(0), ring.remainingMs);
        return () => clearTimeout(timer);
    }, [ring]);
    const inCall = state.status === 'in-call' || state.status === 'reconnecting' || state.status === 'joining';
    const ringingHere = Boolean(ring) && ringUntil > 0 && ring.userId !== userId && !inCall
        && Boolean(eligible) && Boolean(call?.enabled) && dismissedRing !== ringKey
        && participants.length < (call?.max || 5);

    const join = useCallback(() => sessionRef.current?.join(), []);
    const leave = useCallback(() => sessionRef.current?.leave(), []);
    const toggleAudio = useCallback(() => sessionRef.current?.toggleAudio(), []);
    const toggleVideo = useCallback(() => sessionRef.current?.toggleVideo(), []);
    const decline = useCallback(() => {
        setDismissedRing(ringKey);
        sessionRef.current?.decline();
    }, [ringKey]);

    /**
     * The rematch has started and the server has moved the call to it. Keep
     * this session (and every connection) and show the same people while the
     * new game's own list is on its way.
     */
    const carryTo = useCallback((nextGameId) => {
        const session = sessionRef.current;
        if (!session || (session.status !== 'in-call' && session.status !== 'reconnecting')) {
            return false;
        }
        if (session.gameId === String(nextGameId)) {
            return true;
        }
        session.carryTo(nextGameId);
        if (call) {
            dispatch(setCallParticipants({ ...call, gameId: String(nextGameId), ringing: null }));
        }
        return true;
    }, [call, dispatch]);

    return {
        ...state,
        enabled: Boolean(call?.enabled) && current,
        // Whether to offer the call to this player at all.
        available: Boolean(call?.enabled) && current && Boolean(eligible),
        max: call?.max || 5,
        participants,
        subscribed,
        // Someone else started the call and it is ringing on this screen.
        incoming: ringingHere ? { name: ring.name } : null,
        // This player started it and the others' screens are still ringing.
        ringingOthers: Boolean(ring) && ringUntil > 0 && ring.userId === userId && participants.length < 2,
        join,
        leave,
        decline,
        carryTo,
        toggleAudio,
        toggleVideo
    };
};

export default useCall;
